/**
 * Los cinco estados obligatorios · `BFF_BO.md` §6
 *
 * «Sin esto, todo queda al 90 % para siempre.» Están aquí, como componentes, para
 * que usarlos sea más fácil que improvisarlos — que es la única forma de que una
 * regla así se cumpla al sexto mes.
 */
import type { ReactNode } from 'react';
import type { Problema } from '@/shared/api/errores';
import { TextoConEnfasis } from '@/shared/ui/TextoConEnfasis';

/**
 * Cargando: **esqueleto con la forma del contenido, no una rueda centrada.**
 *
 * Una rueda no dice nada; un esqueleto con tres filas dice «viene una lista de
 * tres cosas» y el ojo ya está donde va a estar el dato.
 */
export function Cargando({
  filas = 3,
  etiqueta = 'Cargando',
}: {
  filas?: number;
  etiqueta?: string;
}) {
  return (
    <div className="esqueleto" aria-busy="true" aria-label={etiqueta}>
      {Array.from({ length: filas }, (_, i) => (
        <div key={i} className="esqueleto-fila" />
      ))}
    </div>
  );
}

/**
 * Vacío: **qué es esta pantalla y la acción que la llena.**
 *
 * «Todavía no hay alumnos» a secas deja a alguien mirando. «Todavía no hay
 * alumnos → Importar Excel» lo saca de ahí.
 */
export function Vacio({
  titulo,
  explicacion,
  accion,
}: {
  titulo: string;
  explicacion: string;
  accion?: ReactNode;
}) {
  return (
    <div className="hueco">
      <h2>{titulo}</h2>
      <p>{explicacion}</p>
      {accion && <div className="hueco-accion">{accion}</div>}
    </div>
  );
}

/**
 * Error: **el `detail` del problema tal cual**, y reintentar. En un 500, el
 * `trace_id` con botón de copiar — es lo que convierte «no va» en un aviso que
 * podemos buscar en los registros.
 */
export function Error({
  problema,
  reintentar,
}: {
  problema: Problema;
  reintentar?: () => void;
}) {
  const copiar = () => {
    if (problema.trace_id)
      void navigator.clipboard?.writeText(problema.trace_id);
  };

  return (
    <div className="hueco hueco-error" role="alert">
      <h2>{problema.title}</h2>
      {problema.detail && (
        <p>
          <TextoConEnfasis texto={problema.detail} />
        </p>
      )}

      {problema.errors && problema.errors.length > 0 && (
        <ul className="campos">
          {problema.errors.map((e) => (
            <li key={e.field}>
              <b>{e.field}</b>: {e.message}
            </li>
          ))}
        </ul>
      )}

      <div className="hueco-acciones">
        {reintentar && (
          <button type="button" onClick={reintentar}>
            Reintentar
          </button>
        )}
        {problema.trace_id && (
          <button type="button" className="btn-plano" onClick={copiar}>
            Copiar referencia
          </button>
        )}
      </div>

      {problema.trace_id && (
        <p className="traza">
          Referencia: <code>{problema.trace_id}</code>
        </p>
      )}
    </div>
  );
}

/**
 * Sin permiso: **la sección no aparece.** Esto es solo para quien llega por URL.
 *
 * Ocultar es cortesía y el servidor es quien decide, pero un menú lleno de
 * opciones que dan 403 es una aplicación que parece rota.
 */
export function SinPermiso({ queEs = 'Esta sección' }: { queEs?: string }) {
  return (
    <div className="hueco">
      <h2>{queEs} no es para tu rol</h2>
      <p>
        Si necesitas entrar aquí, pídeselo al dueño del club: es quien reparte
        los permisos.
      </p>
    </div>
  );
}

/**
 * Club suspendido · §6.5
 *
 * Aviso persistente en la cabecera y formularios en solo lectura. **No se apaga
 * la aplicación**: un club que se retrasa un mes en pagarnos no puede dejar a 120
 * familias sin ver el historial de sus hijos. Eso nos hunde la reputación mucho
 * más de lo que vale la cuota.
 */
export function AvisoSuspendido({ contacto }: { contacto: string }) {
  return (
    <div className="banda-suspendido" role="status">
      <strong>La cuenta del club está suspendida por un impago.</strong> Puedes
      seguir consultando todo; para volver a guardar cambios, escribe a{' '}
      <a href={`mailto:${contacto}`}>{contacto}</a>.
    </div>
  );
}

/**
 * El pago a Kiap · `endpoints.md` §A.2.1
 *
 * Tres avisos, de menos a más:
 *
 * - **Vence pronto** (siete días o menos): solo a quien paga, y sin alarma.
 * - **Vencido, dentro de la gracia**: todo funciona, y hay que ponerse al día.
 *   Con la fecha en que el panel pasará a solo lectura, que es lo que hace que se
 *   lea.
 * - **Gracia agotada**: el panel está en solo lectura. Lo ve todo el equipo,
 *   porque a todos les va a fallar el botón de guardar; el detalle del pago, solo
 *   quien paga. Y dice lo que **sigue** funcionando: el maestro tiene que saber que
 *   la app del tatami no se ha parado.
 */
export function AvisoDelPago({
  situacion,
  pagadoHasta,
  finDeLaGracia,
  diasQueQuedan,
  vePago,
  contacto,
}: {
  situacion: 'current' | 'due' | 'lapsed' | 'none';
  pagadoHasta: string;
  finDeLaGracia: string;
  diasQueQuedan: number | null;
  /** Dueño y administrativos. */
  vePago: boolean;
  contacto: string;
}) {
  const correo = <a href={`mailto:${contacto}`}>{contacto}</a>;

  if (situacion === 'lapsed') {
    return (
      <div className="banda-suspendido" role="status">
        <strong>
          El panel está en solo lectura por un pago pendiente con Kiap.
        </strong>{' '}
        Puedes consultarlo todo, y la app del maestro y la de las familias
        siguen funcionando.
        {vePago ? (
          <>
            {' '}
            El servicio estaba cubierto hasta el {pagadoHasta}: en cuanto os
            pongáis al día, todo vuelve a funcionar. Si ya lo habéis pagado,
            escribid a {correo}.
          </>
        ) : (
          <> Para volver a guardar cambios, el club tiene que ponerse al día.</>
        )}
      </div>
    );
  }

  if (!vePago) return null;

  if (situacion === 'due') {
    return (
      <div className="banda-suspendido" role="status">
        <strong>Tenéis un pago pendiente con Kiap.</strong> El servicio estaba
        cubierto hasta el {pagadoHasta}, y todo sigue funcionando con normalidad
        hasta el {finDeLaGracia}
        {diasQueQuedan !== null
          ? ` (${diasQueQuedan === 1 ? 'queda 1 día' : `quedan ${diasQueQuedan} días`})`
          : ''}
        ; a partir de entonces el panel pasará a solo lectura. Si ya lo habéis
        pagado, escribid a {correo}.
      </div>
    );
  }

  if (situacion === 'current' && diasQueQuedan !== null && diasQueQuedan <= 7) {
    return (
      <div className="banda-aviso" role="status">
        Vuestro plan está pagado hasta el {pagadoHasta}.
      </div>
    );
  }

  return null;
}
